"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, Send } from "lucide-react";
import Navbar from "./Navbar";
import { Button } from "@/components/ui/button";

export default function SupportPageClient() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/support", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit");
      }

      setSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch {
      setError("Failed to submit. Please try again later or refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Navbar />

      <main className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-4xl">
          <Button asChild variant="outline" className="mb-8 min-h-11">
            <Link href="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>

          <div className="mb-12 text-center">
            <div className="mb-4 flex items-center justify-center gap-4">
              <div className="h-px w-12 bg-gray-900"></div>
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-gray-900">
                Contact Us
              </span>
              <div className="h-px w-12 bg-gray-900"></div>
            </div>
            <h1 className="mb-4 text-4xl font-black leading-tight text-gray-900 md:text-6xl">
              How Can We Help?
            </h1>
            <p className="mx-auto max-w-2xl text-xl font-light text-gray-600">
              Have a question or need assistance? We&apos;re here to help!
            </p>
          </div>

          <div className="mb-8 rounded-xl border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50 p-6">
            <p className="text-center text-gray-700">
              <strong>Prefer email?</strong> You can reach us directly at{" "}
              <a
                href="mailto:support@storly.world"
                className="font-semibold text-blue-600 hover:text-blue-700"
                aria-label="Email support at storly"
                title="Send email to support"
              >
                support@storly.world
              </a>
            </p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              {[
                { href: "/ai-bedtime-stories", label: "AI Bedtime Stories" },
                { href: "/story-book-creator", label: "Story Book Creator" },
                { href: "/audio-stories", label: "Audio Stories" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="rounded-full border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:border-blue-400 hover:text-blue-800"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border-2 border-gray-200 bg-white shadow-xl">
            <div className="h-2 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>

            <div className="p-8 md:p-12">
              {submitted ? (
                <div className="py-12 text-center">
                  <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-500">
                    <svg
                      className="h-10 w-10 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h2 className="mb-4 text-3xl font-black text-gray-900">
                    Message Sent!
                  </h2>
                  <p className="mb-8 text-lg text-gray-600">
                    Thank you for reaching out. We&apos;ll get back to you within
                    24-48 hours.
                  </p>
                  <Button
                    onClick={() => setSubmitted(false)}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <>
                  <h2 className="mb-2 text-2xl font-black text-gray-900">
                    Send Us a Message
                  </h2>
                  <p className="mb-8 text-gray-600">
                    Fill out the form below and we&apos;ll respond as soon as possible.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="mb-2 block text-sm font-bold text-gray-900"
                      >
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 transition-colors focus:border-blue-500 focus:outline-none"
                        placeholder="Type your full name here"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="email"
                        className="mb-2 block text-sm font-bold text-gray-900"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 transition-colors focus:border-blue-500 focus:outline-none"
                        placeholder="Enter your email address for us to get back to you"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="mb-2 block text-sm font-bold text-gray-900"
                      >
                        Subject *
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 transition-colors focus:border-blue-500 focus:outline-none"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="mb-2 block text-sm font-bold text-gray-900"
                      >
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full resize-none rounded-xl border-2 border-gray-200 px-4 py-3 transition-colors focus:border-blue-500 focus:outline-none"
                        placeholder="Tell us more about your question or issue..."
                      />
                    </div>

                    {error && (
                      <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
                        <p className="text-sm font-medium text-red-600">{error}</p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-xl border-0 bg-gradient-to-r from-blue-600 to-purple-600 py-6 text-lg font-bold text-white shadow-lg transition-all hover:from-blue-700 hover:to-purple-700 hover:shadow-xl"
                    >
                      {loading ? (
                        <>
                          <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
